import gsap from 'gsap';

import { Container, Point, Sprite, Texture } from 'pixi.js';
import { MonoBehavior } from './core/monoBehavior';
import { getAsset } from './preloadGameAssets';
import { lerp } from './core/lerp';
import { CardColors } from 'constants/cards';
import { addToUI } from './core/Playable2DRootContainer';
import { Layout } from './LayoutManager';
import { Power1 } from 'gsap';
import { Time } from './core/pixiRenderer';
import { Store } from './store/gameStore';
import { ParticlesAtlas } from './assets/generated';

export class CardParticles extends MonoBehavior {
    private particleContainer: Container;
    private particles = [] as ParticleItem[];
    private color: CardColors;

    constructor(parentContainer: Container, color: CardColors) {
        super();
        
        // Skip card particles entirely on low-end devices
        if (Store.lowPerformanceDevice) {
            return;
        }
        
        this.color = color;
        this.loadParticle = this.loadParticle.bind(this);
        this.particleContainer = new Container();
        const pos = new Point();
        parentContainer.getGlobalPosition(pos);
        this.particleContainer.position = pos;
        addToUI(this.particleContainer);
    }
    start(): void {
        if (Store.lowPerformanceDevice) {
            return;
        }
        setTimeout(() => {
            this.loadParticle();
        }, 160);
    }
    async loadParticle() {
        const particleAssets = {
            [CardColors.CLUBS]: 'clubs_face.png',
            [CardColors.SPADES]: 'spades_face.png',
            [CardColors.HEARTS]: 'hearts_face.png',
            [CardColors.DIAMONDS]: 'diamonds_face.png',
        };
        const asset = await ParticlesAtlas.getTextureByName(particleAssets[this.color]);
        const txt = new Texture(asset);
        let particleCount = 10;
        while (particleCount > 0) {
            this.particles.push(new ParticleItem(this.particleContainer, txt, particleCount));
            particleCount--;
        }
        setTimeout(this.destroy, 2000);
    }
}

class ParticleItem extends MonoBehavior {
    targetPosition: Point;
    sprite: Sprite;
    startTime = 0;
    container: Container;
    popActive = false;
    yoyo = [] as gsap.core.Tween[];
    duration: number;
    startPosition: any;
    rotation: number;
    constructor(particleContainer: Container, texture: Texture, fractal: number) {
        super();

        const cardSize = Layout.getCardSize();
        this.startTime = performance.now();
        this.rotation = lerp(-0.01, 0.01, Math.random());

        this.sprite = new Sprite(texture);
        this.sprite.anchor.set(0.5);
        this.container = new Container();
        this.container.scale.set(0);
        const axis = fractal < 4 ? 'x' : 'y';
        const side = fractal % 2 === 0 ? -1 : 1;
        if (axis === 'x') {
            this.container.position.x = lerp(-cardSize.width / 2, cardSize.width / 2, Math.random());
            this.container.position.y =
                (side * cardSize.height) / 2 + lerp(-cardSize.width / 12, cardSize.width / 12, Math.random());
        } else {
            this.container.position.x =
                (side * cardSize.width) / 2 + lerp(-cardSize.height / 12, cardSize.height / 12, Math.random());

            this.container.position.y = lerp(-cardSize.height / 2, cardSize.height / 2, Math.random());
        }
        this.targetPosition = new Point(
            this.container.position.x + cardSize.width * lerp(-0.15, 0.15, Math.random()),
            this.container.position.y + cardSize.height * lerp(-0.15, 0.15, Math.random())
        );
        this.startPosition = this.container.position.clone();
        this.container.addChild(this.sprite);
        const particleScale = Layout.getParticleScale();

        this.sprite.scale.set(lerp(particleScale * 0.5, particleScale, Math.random()));
        particleContainer.addChild(this.container);
        const duration = lerp(0.05, 0.15, Math.random());
        this.duration = duration * 1000;
        gsap.to(this.container.scale, {
            x: 0.4,
            y: 0.4,
            duration: duration,
            delay: lerp(0.1, 0.2, Math.random()),
        }).then(() => {
            this.yoyo.push(
                gsap.to(this.container.scale, {
                    x: 0.7,
                    y: 0.7,
                    duration: lerp(1.3, 1.5, Math.random()),
                })
            );
        });

        gsap.to(this.container, {
            alpha: 0, // Fade out the container
            duration: 0.7,
            delay: 0.5,
            onComplete: () => {
                this.destroy();
            },
        });
    }

    update(): void {
        const prc = (performance.now() - this.startTime) / 1200;
        this.container.position.x = lerp(this.startPosition.x, this.targetPosition.x, Power1.easeOut(prc));
        this.container.position.y = lerp(this.startPosition.y, this.targetPosition.y, Power1.easeOut(prc));
        this.sprite.rotation += this.rotation * Time.deltaTime;
    }

    destroy() {
        this.yoyo.forEach((tween) => {
            tween.kill();
        });
        super.destroy();
        this.sprite.removeFromParent();
        this.container.removeFromParent();
    }
}
